/**
 * Orchestrator: run every single-series insight module over one timeseries and
 * assemble the result into a {@link TimeseriesInsights} bundle.
 *
 * Several modules are super-linear (Theil–Sen is O(n²); segmentation and CUSUM
 * recurse with linear-cost scans), so the trend/segmentation/level-shift/
 * seasonality stages run on a capped, stride-downsampled copy of the series.
 * Anomaly detection always runs at full resolution — it is cheap and a
 * downsample would average spikes away. Whenever a downsample happens it is
 * recorded in `downsampledTo`, never silent.
 *
 * Cross-signal correlation is deliberately excluded: it is pairwise and only
 * worth its cost on explicit opt-in (see {@link laggedCrossCorrelation}).
 */
import { detectAnomalies } from './anomalies.ts';
import { detectLevelShifts } from './level-shifts.ts';
import { computeSeasonality } from './seasonality.ts';
import { segmentSeries } from './segmentation.ts';
import { computeTrend } from './trend.ts';
import type { Sensitivity, TimeseriesInsights, TimeseriesPoint } from './types.ts';

/** Default cap on the series length fed to the quadratic-cost stages. */
const DEFAULT_MAX_POINTS = 1500;

export interface TimeseriesInsightsOptions {
  /** Significance threshold for trend tests. Default 0.05. */
  alpha?: number;
  /** Sensitivity shared by segmentation and level-shift detection. Default `'medium'`. */
  sensitivity?: Sensitivity;
  /** Max points fed to the quadratic-cost stages; larger series are downsampled. Default 1500. */
  maxPoints?: number;
}

/**
 * Stride-subsample to at most `cap` points, always keeping the final point so
 * the time span and end-state are preserved. Returns the input untouched when
 * it already fits.
 */
function downsample(points: TimeseriesPoint[], cap: number): TimeseriesPoint[] {
  if (points.length <= cap) {
    return points;
  }
  const stride = Math.ceil(points.length / cap);
  const kept = points.filter((_, i) => i % stride === 0);
  const last = points.at(-1);
  return last && kept.at(-1) !== last ? [...kept, last] : kept;
}

/** Compute the full single-series insight bundle. Returns `null` for an empty series. */
export function computeTimeseriesInsights(
  points: TimeseriesPoint[],
  options: TimeseriesInsightsOptions = {},
): TimeseriesInsights | null {
  const sorted = [...points].sort((a, b) => a.timestamp - b.timestamp);
  const first = sorted[0];
  const last = sorted.at(-1);
  if (!first || !last) {
    return null;
  }

  const alpha = options.alpha ?? 0.05;
  const sensitivity = options.sensitivity ?? 'medium';
  const cap = options.maxPoints ?? DEFAULT_MAX_POINTS;

  // Full-resolution stage: cheap and fidelity-sensitive.
  const anomalies = detectAnomalies(sorted);

  // Capped stages: super-linear cost.
  const working = downsample(sorted, cap);
  const downsampledTo = working.length < sorted.length ? working.length : null;

  return {
    pointCount: sorted.length,
    start: first.timestamp,
    end: last.timestamp,
    trend: computeTrend(working, { alpha }),
    segments: segmentSeries(working, { sensitivity, alpha }),
    levelShifts: detectLevelShifts(working, { sensitivity }),
    anomalies,
    seasonality: computeSeasonality(working),
    downsampledTo,
  };
}
